import { Box, CircularProgress } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { ImageAppHistoryItem, MessageType } from "../../../../Helper";
import { ImageSuiteHistoryItem } from "../Components/ImageSuiteHistoryItem";
import { DisplayHistoryItemDataModal } from "../Components/DisplayHistoryItemDataModal";
import SignalRService from "../../../one-query/services/SignalRService";
import {
  GetOptionsRequest,
  useRegenerateImageItem,
  useSuggestionSuiteApps,
} from "../../../../store/Apis/SuitesApps/SuiteHistroyApi";
import { useGetUserCredits } from "../../../../store/Apis/StripeApi";
import EmptySuiteHistory from "../../../VideoSuite/VideoSuiteHistory/Components/EmptySuiteHistory";
import { getBaseUrl } from "../../Helper";
import { VideoDetailsView } from "../../../VideoSuite/Components/Shared/VideoSuiteHelper";
export interface SuggestionApp {
  app: string;
  suggestions: string[];
}

interface ContextType {
  ImageAppHistoryItems: ImageAppHistoryItem[];
  DeleteImageItem: (itemId: string) => void;
  isImageSuiteHistoryLoading: boolean;
  isDeleteImageItemLoading: boolean;
  isImageItemDeleted:boolean;
  isSuccessImageSuiteHistory: boolean;
  GetImageSuiteHistory: (options?: GetOptionsRequest) => void;
}

export const ImageSuiteHistoryContainer = () => {
  const [openDisplayItemModal, setOpenDisplayItemModal] = useState(false);
  const [isRegenerateItemLoading, setIsRegenerateItemLoading] = useState(false);
  const [regenerateItemOutput, setRegenerateItemOutput] = useState("");
  const [suggestionsImageApps, setSuggestionImageApps] = useState<
    SuggestionApp[]
  >([]);
  const {
    ImageAppHistoryItems,
    DeleteImageItem,
    isImageSuiteHistoryLoading,
    isDeleteImageItemLoading,
    isSuccessImageSuiteHistory,
    isImageItemDeleted,
    GetImageSuiteHistory
  } = useOutletContext<ContextType>();
  const [activeHistoryItem, setActiveHistoryItem] =
    useState<ImageAppHistoryItem>();
  const {
    mutate: ImageSuiteRegenerateItem,
    isPending: isRegenerateImageItemLoading,
  } = useRegenerateImageItem();
  const { refetch: GetSuggestionsImageApps, data: SuggestionsImageAppsData } =
    useSuggestionSuiteApps();
  const getUserCredits = useGetUserCredits();
  const isEmpty = ImageAppHistoryItems?.length === 0;
  useEffect(() => {
    GetSuggestionsImageApps();
  }, []);

  useEffect(() => {
    if (SuggestionsImageAppsData) {
      setSuggestionImageApps(SuggestionsImageAppsData);
    }
  }, [SuggestionsImageAppsData]);

  // handle regenerate item endpoint fire.
  const handleRegenerateImageHistoryItem = async () => {
    const connectionId = await SignalRService.getInstance().getConnectionId();

    const regenerateRequestData = {
      historyId: activeHistoryItem?.id || "",
      connectionId: connectionId,
    };
    ImageSuiteRegenerateItem(regenerateRequestData);
  };

  const handleActiveHistoryItem = (item?: ImageAppHistoryItem) => {
    setActiveHistoryItem(item);
    setOpenDisplayItemModal(true);
    setRegenerateItemOutput("");
  };

  const handleCloseDisplayItemModal = () => {
    setOpenDisplayItemModal(false);
  };

  useEffect(() => {
    if (isRegenerateImageItemLoading) {
      setIsRegenerateItemLoading(true);
      setRegenerateItemOutput("");
    }
  }, [isRegenerateImageItemLoading]);

  const handleGenerateImageSuitesSignalR = useCallback(
    (
      modelName: string,
      modelVersion: string,
      message: string,
      tag: string,
      conversationId: string,
      messageId: string,
      messageType: number
    ) => {
      if (message === "**/**Finished**/**") {
        getUserCredits();
        setIsRegenerateItemLoading(false);
        GetImageSuiteHistory({})
      } else {
        if (
          messageType === MessageType.ImageSuiteGenerateImage ||
          messageType === MessageType.ImageSuiteGenerateIcon ||
          messageType === MessageType.ImageSuiteGenerateIllustration ||
          messageType === MessageType.ImageSuiteGenerateLogo ||
          messageType === MessageType.ImageSuiteBackgroundRemover ||
          messageType === MessageType.ImageSuiteMicroPeople ||
          messageType === MessageType.ImageSuiteTinyFigures ||
          messageType === MessageType.ImageSuiteRestoreImage
        ) {
          setIsRegenerateItemLoading(true);
            setRegenerateItemOutput(getBaseUrl(message));
        }
      }
    },
    []
  );

  const setupSignalRConnection = useCallback(() => {
    const connection = SignalRService.getInstance().connection;
    connection.on("SendModelMessage", handleGenerateImageSuitesSignalR);
    return () => {
      connection.off("SendModelMessage", handleGenerateImageSuitesSignalR);
    };
  }, [handleGenerateImageSuitesSignalR]);

  useEffect(() => {
    const cleanup = setupSignalRConnection();
    return () => {
      cleanup();
    };
  }, [setupSignalRConnection]);

  return (
    <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      height: "100vh",
      overflow: "hidden",
    }}
  >
      {isImageSuiteHistoryLoading&&!isSuccessImageSuiteHistory ? (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress sx={{ color: "#12b6ea" }} />
        </Box>
      ) : isSuccessImageSuiteHistory && !isEmpty ? (
        <Box className="video-suite-history-container"
        sx={{ placeItems: { xs: "center", sm: "flex-start" }}}
        >
          {ImageAppHistoryItems?.map((item, index) => (
            <Box key={index}>
              <ImageSuiteHistoryItem
                imageAppItem={item}
                DeleteImageItem={DeleteImageItem}
                isImageItemDeleted={isImageItemDeleted}
                isDeleteImageItemLoading={isDeleteImageItemLoading}
                handleCloseDisplayItemModal={handleCloseDisplayItemModal}
                handleActiveHistoryItem={handleActiveHistoryItem}
                cardView={VideoDetailsView.history}
              />
            </Box>
          ))}
        </Box>
      ):isSuccessImageSuiteHistory && isEmpty?(
          <EmptySuiteHistory 
            suiteRouting="/suites/image-suite"
            suiteClass="empty-image-history-image"
            suiteHeader="No Images found"
            suiteMessage="Explore all image apps and create the creative ones with Expertex.AI"
          />
      ):null}
      {openDisplayItemModal && (
        <DisplayHistoryItemDataModal
          open={openDisplayItemModal}
          itemId={activeHistoryItem?.id || ""}
          handleClose={handleCloseDisplayItemModal}
          handleRegenerateItem={handleRegenerateImageHistoryItem}
          isRegenerateItemLoading={isRegenerateItemLoading}
          regenerateItemOutput={regenerateItemOutput}
          suggestionsImageApps={suggestionsImageApps}
        />
      )}
    </Box>
  );
};
